/* eslint-disable no-underscore-dangle */
import { queryActivityAndPageByMaterialNo, queryActivityMaterialList, queryProductListByMaterialNo, queryProductMaterialList } from "@/api/product";
import { ActivityModel, IConference3Model, ProductModel } from "@akc/biz-api";
import { useEffect, useRef, useState } from "react";

// 档期扩展
export interface IActivityModelCust extends ActivityModel {
  cardType:"ACTIVITY" | "CONFERENCE";
}

// 会场扩展
export interface IConference3ModelCust extends IConference3Model {
  cardType:"ACTIVITY" | "CONFERENCE";
}

// 活动或会场
export type IActivityOrConferenceModelCust =IActivityModelCust | IConference3ModelCust;

/**
 * 查询更多商品、活动弹窗相关
 * 使用场景包括：逛一逛推荐tab、商品商品大卡页、素材视频大卡页
 * @returns 
 */
export const  useMoreProductList=({
  queryBy,
  activitySpuId,
  materialNo,
}: {
  queryBy: "PRODUCT" | "MATERIAL";
  activitySpuId?: string;
  materialNo?: string;
}) => {
  // 当前屏幕🐷商品的spuId，用于查询关联商品和活动
  const [productListLoading, setProductListLoading] = useState(false);
  // 是否同时有商品和档期tab
  const [tabs, setTabs] = useState<{title:string, key:string}[]>([]);

  
  // 更多商品列表
  const [productList, setProductList] = useState<ProductModel[]>([]);
  // searchParams参数（含页码)
  const prdPageNumRef=useRef<number>(1);
  const [hasMorePrd, setHasMorePrd] = useState(true);
  const [prdInitEnd, setPrdInitEnd]= useState(false);
  const prdSearchRequestIdRef = useRef<string>(`${Math.floor(Math.random() * 10000000000)}`);

  // 更多档期及会场列表
  const [activityList, setActivityList] = useState<Array<IActivityOrConferenceModelCust>>([]);
  const activitySearchRequestIdRef = useRef<string>(`${Math.floor(Math.random() * 10000000000)}`);
  const [activityInitEnd, setActivityInitEnd]= useState(false);

  /**
   * 根据商品或素材查询关联商品，不同接口，返回值结构一致
   * @returns 
   */
  const getMoreProductPromise=async () => {
    // 根据商品查关联商品
    if(queryBy === "PRODUCT") {
      return queryProductMaterialList({
        pageId:"H5_ProductList",
        searchParams:{
          scene: "INDEX",
          sortModel: 1,
          sortType: 2,
          pageNum: prdPageNumRef.current,
          pageSize: 10,
          searchRequestId: prdSearchRequestIdRef.current,
        },
        mainActivitySpuId:activitySpuId,
        materialNo
      });
    }
    // 否则根据素材查询
    return queryProductListByMaterialNo({
      pageNo: prdPageNumRef.current,
      pageSize: 10,
      materialNo,
    });
  };
  // 加载一页主商品的关联商品数据
  const handleMoreProductList = async () => {
    if(productListLoading) {
      return;
    }
    setProductListLoading(true);
    try {
      const rsp = await getMoreProductPromise();
      if (Array.isArray(rsp?.result)) {
        setProductList(prev => [...prev, ...(rsp.result || [])]);
      }
      setHasMorePrd(rsp?.hasNext || false);
      // 参数页码加1
      prdPageNumRef.current++;
    } catch (error) {
      console.error(error);
      setHasMorePrd(false);
    } finally {
      setProductListLoading(false);
      !prdInitEnd && setPrdInitEnd(true);
    }
  };

  const getMoreActivityPromise=async () => {
    // 根据商品查关联档期
    if(queryBy === "PRODUCT") {
      return queryActivityMaterialList({ 
        pageId:"H5_ScheduleList",
        searchParams:{
          scene: "INDEX",
          sortModel: 1,
          sortType: 2,
          searchRequestId: activitySearchRequestIdRef.current,
        },
        mainActivitySpuId:activitySpuId,
        materialNo,
      });
    }
    // 否则根据素材查询
    return queryActivityAndPageByMaterialNo({materialNo});
  };


  // 加载一页主商品的关联活动数据，不支持分页
  const handleMoreActivityList = async () => {
    if(productListLoading) {
      return;
    }
    setProductListLoading(true);
    try {
      const rsp = await getMoreActivityPromise();
      const {activityListVOList=[],pageBOList=[]}=rsp||{};
      // 合并展示
      const moreActivityList=[
        ...((activityListVOList || []).map(item => ({...item,cardType:"ACTIVITY"} as IActivityModelCust))),
        ...(pageBOList || []).map(item => ({...item,cardType:"CONFERENCE"} as IConference3ModelCust)),
      ] as Array<IActivityOrConferenceModelCust>;
      setActivityList(moreActivityList);
    } catch (error) {
      console.error(error);
    } finally {
      setProductListLoading(false);
      !activityInitEnd && setActivityInitEnd(true);
      }
  };

  // 清空关联商品
  const resetState = () => {
    setProductList([]);
    prdPageNumRef.current=1;
    setHasMorePrd(true);
    setActivityList([]);
  };

  useEffect(() => {
    resetState();
  },[activitySpuId,materialNo,queryBy]);

  useEffect(() => {
    if(!prdInitEnd || !activityInitEnd) {
      return;
    }
    if(productList.length>0 && activityList.length<=0){
      setTabs([{ key:"PRODUCT", title:"商品"  },]);
    }else if(productList.length<=0 && activityList.length>0){
      setTabs([{ key:"ACTIVITY", title:"活动"  },]);
    }else{
      setTabs([
        { key:"PRODUCT", title:"商品"  },
        { key:"ACTIVITY", title:"活动"  },
      ]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[productList,activityList,prdInitEnd,activityInitEnd]);

  return {
    productListLoading,
    resetState,
    tabs,

    // 商品相关
    productList,
    hasMorePrd,
    handleMoreProductList,
    prdSearchRequestIdRef,

    // 档期相关
    activityList,
    handleMoreActivityList,
    activitySearchRequestIdRef,
  };
};
